import { useTranslation } from "react-i18next";
import {
  UserPlus,
  ArrowRightLeft,
  ShieldAlert,
  Wallet,
  Crown,
  Pencil,
} from "lucide-react";
import { relativeTime } from "@/shared/lib/time";
import { ActivityFeed } from "./ActivityFeed";
import { SectionTitle, formatBalance } from "./fields";
import { useCustomerTimeline } from "../hooks";
import type { TimelineEvent } from "../types";

// ── Timeline (task 0066) ─────────────────────────────────────────────────────
// The customer's own app activity (ActivityFeed, task 0153) leads the tab. Below it,
// one round-trip to /timeline/, split into two SectionTitle groups: customer
// lifecycle (registered + status changes) and support-operator actions. The label
// and icon both derive from (kind, action, extra) so the backend constants stay the
// single source of truth — see the action strings in apps/logs/constants.py.

/** Pick the i18n label key for an event. "" → fall back to the raw action string. */
function timelineLabelKey(e: TimelineEvent): string {
  if (e.kind === "registered") return "timeline.registered";
  if (e.kind === "status_change") return "timeline.statusChange";
  const action = e.action ?? "";
  if (action.includes("developer_toggle")) {
    return e.extra?.is_developer ? "timeline.developerOn" : "timeline.developerOff";
  }
  if (action.includes("balance_adjust")) {
    return e.extra?.direction === "debit" ? "timeline.balanceDebit" : "timeline.balanceCredit";
  }
  if (action.includes("identity_correct")) return "timeline.identityCorrected";
  if (action.includes("premium")) return "timeline.premiumGranted";
  return "";
}

function timelineIcon(e: TimelineEvent) {
  if (e.kind === "registered") return UserPlus;
  if (e.kind === "status_change") return ArrowRightLeft;
  const action = e.action ?? "";
  if (action.includes("developer_toggle")) return ShieldAlert;
  if (action.includes("balance_adjust")) return Wallet;
  if (action.includes("identity_correct")) return Pencil;
  if (action.includes("premium")) return Crown;
  return ArrowRightLeft;
}

export function TimelineSection({ customerId }: { customerId: number }) {
  const { t } = useTranslation();
  const { data, isLoading } = useCustomerTimeline(customerId);

  if (isLoading) {
    return (
      <>
        <ActivityFeed customerId={customerId} />
        <SectionTitle className="mt-2">{t("timeline.sectionHistory")}</SectionTitle>
        <div className="mb-5 flex flex-col gap-2">
          {[0, 1, 2].map((k) => (
            <div key={k} className="h-9 animate-pulse rounded-lg bg-grey-100" />
          ))}
        </div>
      </>
    );
  }

  const events = data ?? [];
  // Lifecycle group = everything that isn't an operator action; the rest is the
  // support-action feed. Both already arrive newest-first from the backend.
  const history = events.filter((e) => e.kind !== "support_action");
  const actions = events.filter((e) => e.kind === "support_action");

  return (
    <>
      <ActivityFeed customerId={customerId} />
      <SectionTitle className="mt-2">{t("timeline.sectionHistory")}</SectionTitle>
      <TimelineGroup events={history} />
      <SectionTitle className="mt-2">{t("timeline.sectionSupportActions")}</SectionTitle>
      <TimelineGroup events={actions} />
    </>
  );
}

function TimelineGroup({ events }: { events: TimelineEvent[] }) {
  const { t } = useTranslation();
  if (events.length === 0) {
    return <p className="mb-5 text-[13px] text-grey-500">{t("timeline.empty")}</p>;
  }
  return (
    <ul className="mb-5 flex flex-col gap-3">
      {events.map((e, i) => (
        <TimelineRow key={`${e.kind}-${e.timestamp}-${i}`} event={e} />
      ))}
    </ul>
  );
}

function TimelineRow({ event }: { event: TimelineEvent }) {
  const { t } = useTranslation();
  const Icon = timelineIcon(event);
  const labelKey = timelineLabelKey(event);
  const label = labelKey ? t(labelKey) : event.action ?? "";

  // Second line: status diff (old → new) or the balance-adjust amount + reason.
  let detail: string | null = null;
  if (event.kind === "status_change" && (event.old || event.new)) {
    detail = `${event.old ?? "—"} → ${event.new ?? "—"}`;
  } else if (
    event.kind === "support_action" &&
    event.action?.includes("balance_adjust") &&
    event.extra
  ) {
    const parts: string[] = [];
    if (typeof event.extra.amount === "string") parts.push(formatBalance(event.extra.amount));
    if (typeof event.extra.reason === "string" && event.extra.reason.trim()) {
      parts.push(t("timeline.reason", { reason: event.extra.reason }));
    }
    detail = parts.join(" · ") || null;
  }

  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-pill bg-grey-50 text-grey-500">
        <Icon size={15} strokeWidth={1.8} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[13.5px] font-semibold text-grey-900">{label}</span>
          <span className="flex-none text-[12px] text-grey-400">
            {relativeTime(event.timestamp, t)}
          </span>
        </div>
        {detail && <div className="mt-0.5 text-[12.5px] text-grey-600">{detail}</div>}
        {event.actor_name && (
          <div className="mt-0.5 text-[12px] text-grey-400">
            {t("timeline.by", { name: event.actor_name })}
          </div>
        )}
      </div>
    </li>
  );
}
